import { Component, signal } from '@angular/core';
import { Client, ClientText, EMPTY_CLIENT, ClientAccountType, _, LanguageCode } from '@mira-avant/mtlibs';
import { ClientService } from '../../../../services/client.service';
import { AngularModule } from '../../../../modules/angular.module';
import { NavigationService } from '../../../../services/navigation.service';
import { ZorroModule } from '../../../../modules/zorro.module';
import { ClientFormComponent } from '../../../forms/standard/client-form/client-form.component';
import { LanguageSelectorComponent } from '../../language-selector/language-selector.component';
import { v4 } from 'uuid';
import { ClipboardService } from '../../../../services/clipboard.service';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';
import {
	Column,
	CmsTable,
	ENABLE_COPY_COLUMN,
	ENABLE_EDIT_COLUMN,
	ENABLE_DELETE_COLUMN,
	ENABLE_CHECKBOX_COLUMN,
} from '../cms-table/cms-table';
import { RowColumn } from '../cms-table/row-column';
import { HeaderColumn } from '../cms-table/header-column';
import { TableFormContainer } from '../table-form-container/table-form-container';

@Component({
	selector: 'app-client-list',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		ClientFormComponent,
		LanguageSelectorComponent,
		CmsTable,
		RowColumn,
		HeaderColumn,
		TableFormContainer,
	],
	templateUrl: './client-list.component.html',
	styleUrl: './client-list.component.scss',
})
export class ClientListComponent {
	readonly loading = signal<boolean>(true);
	readonly clients = signal<Client[]>([]);
	readonly editClient = signal<Client | undefined>(undefined);
	readonly editLanguage = signal<string>(LanguageCode.EN);
	readonly isAdmin = signal<boolean>(false);
	readonly columns = signal<Column[]>([]);
	readonly isFormShowing = signal<boolean>(false);

	private sub: Subscription | undefined;

	constructor(
		public authService: AuthService,
		public clientService: ClientService,
		public nav: NavigationService,
		public clipboardService: ClipboardService
	) {
		this.sub = this.clientService.clients$.subscribe((clients: Client[]) => {
			this.applyClients(clients);

			this.loading.set(false);
		});

		this.authService.isAdmin$().then((isAdmin: boolean) => {
			this.isAdmin.set(isAdmin);
			this.updateTableDetails();
		});
	}

	private updateTableDetails(): void {
		const columns: Column[] = [];

		if (this.isAdmin()) {
			columns.push(ENABLE_CHECKBOX_COLUMN);
		}

		columns.push({
			key: 'clientName',
			name: 'Name',
			sortOrder: 'ascend',
			sortFn: (a: Client, b: Client) => a.active_text?.name.localeCompare(b.active_text?.name || '') || 0,
			sortDirections: ['ascend', 'descend', null],
			filterMultiple: true,
			filterOptions: this.clients().map(row => ({
				text: row.active_text?.name || '',
				value: row.active_text?.name || '',
			})),
			filterFn: (list: string[], item: Client) => list.some(name => item.active_text?.name.indexOf(name) !== -1),
		});

		if (this.isAdmin()) {
			columns.push(ENABLE_COPY_COLUMN);
			columns.push(ENABLE_EDIT_COLUMN);
			columns.push(ENABLE_DELETE_COLUMN);
		}

		this.columns.set(columns);
	}

	public select($event: object): void {
		const client = $event as Client;
		this.nav.navigate(['my', 'admin', 'lc', { clientUuid: client.uuid }]);
	}

	public copy($event: object): void {
		const client = $event as Client;
		this.clipboardService.copyClient(client);
	}

	public copyBulk($event: object): void {
		const clients = $event as Client[];
		this.clipboardService.copyClients(clients);
	}

	public edit($event: object): void {
		const client = $event as Client;
		this.editClient.set(this.editClient()?.uuid === client.uuid ? undefined : client);
		this.isFormShowing.set(this.editClient() !== undefined);
	}

	public onShowForm(): void {
		this.isFormShowing.set(true);
	}

	public onHideForm(): void {
		this.isFormShowing.set(false);
		this.resetEditState();
	}

	private resetEditState(): void {
		this.editClient.set(undefined);
		this.editLanguage.set(LanguageCode.EN);
	}

	public async delete$($event: unknown): Promise<void> {
		const client = $event as Client;

		this.loading.set(true);

		try {
			await this.clientService.delete$(client.uuid);
			this.clients.set(this.clients().filter(c => c.uuid !== client.uuid));
		} catch (error) {
			console.error('Error deleting client:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async onClientFormSubmit$($event: any): Promise<void> {
		let submitSucceeded = false;
		this.loading.set(true);
		try {
			if (this.editClient()) {
				await this.updateClient$($event);
			} else {
				await this.createNewClient$($event);
			}

			submitSucceeded = true;
		} catch (error) {
			console.error('Error submitting client form:', error);
		} finally {
			this.loading.set(false);
			this.resetEditState();

			if (submitSucceeded) {
				this.isFormShowing.set(false);
			}
		}
	}

	private applyClients(clients: Client[]): void {
		this.clients.set(clients);
		this.updateTableDetails();

		const currentEditClient = this.editClient();
		if (currentEditClient) {
			const refreshedEditClient = clients.find(client => client.uuid === currentEditClient.uuid);
			this.editClient.set(refreshedEditClient);
		}
	}

	private async createNewClient$($event: any): Promise<void> {
		await this.clientService.create$({
			default_lang_id: LanguageCode.EN,
			subdomain: $event.subdomain,
			account_type: $event.accountType || ClientAccountType.K12_SCHOOL,
			allow_section_self_rostering: $event.allowSectionSelfRostering || false,
			allow_clever_sso: $event.allowCleverSso || false,
			clever_sso_district_id: $event.cleverSsoDistrictId || null,
			allow_eduskills: $event.allowEduskills || false,
			eduskills_district_id: $event.eduskillsDistrictId || null,
			data_json: {},
			client_text: [
				{
					default_lang_id: LanguageCode.EN,
					name: $event.name,
					logo_uri: $event.logoUri,
					data_json: {},
					description_short: $event.descriptionShort,
					description_html: $event.descriptionHtml,
					created: new Date(),
					updated: new Date(),
				},
			],
		});
	}

	private async updateClient$($event: any): Promise<void> {
		const currentEditClient = this.editClient();
		if (!currentEditClient) {
			throw new Error('No client to update');
		}

		const clientTexts: ClientText[] = currentEditClient.client_text;

		let clientText: ClientText | undefined = clientTexts.find(text => text.default_lang_id === this.editLanguage());

		if (!clientText) {
			clientText = {
				uuid: v4(),
				client_uuid: currentEditClient.uuid,
				default_lang_id: this.editLanguage(),
				name: $event.name,
				logo_uri: $event.logoUri,
				data_json: {},
				description_short: $event.descriptionShort,
				description_html: $event.descriptionHtml,
				created: new Date(),
				updated: new Date(),
			};

			clientTexts.push(clientText);
		} else {
			clientText.name = $event.name;
			clientText.logo_uri = $event.logoUri;
			clientText.description_short = $event.descriptionShort;
			clientText.description_html = $event.descriptionHtml;
			clientText.data_json = {};
		}

		const props = Object.keys(EMPTY_CLIENT);
		const payload: Client = _.pick(currentEditClient, props) as Client;
		payload.subdomain = $event.subdomain;
		payload.account_type = $event.accountType;
		payload.allow_section_self_rostering = $event.allowSectionSelfRostering;
		payload.allow_clever_sso = $event.allowCleverSso;
		payload.clever_sso_district_id = $event.cleverSsoDistrictId ? $event.cleverSsoDistrictId : null;
		payload.allow_eduskills = $event.allowEduskills ? $event.allowEduskills : false;
		payload.eduskills_district_id = $event.eduskillsDistrictId ? $event.eduskillsDistrictId : null;
		payload.data_json = $event.dataJson ? JSON.parse($event.dataJson) : {};

		await this.clientService.update$(currentEditClient.uuid, {
			...payload,
			client_text: clientTexts,
		});
	}

	public onEditLanguageChange(code: string): void {
		this.editLanguage.set(code);
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
